import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'magic-card',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      #cardRef
      class="group relative rounded-2xl bg-card border border-border p-8"
      [class]="className"
      (mouseenter)="handleMouseEnter()"
      (mouseleave)="handleMouseLeave()"
      (mousemove)="handleMouseMove($event)"
    >
      <!-- Border gradient -->
      <div
        class="pointer-events-none absolute inset-0 rounded-[inherit] bg-border duration-300 group-hover:opacity-100"
        [style.background]="borderGradient"
      ></div>
      
      <!-- Background -->
      <div class="absolute inset-px rounded-[inherit] bg-background"></div>
      
      <!-- Hover gradient -->
      <div
        class="pointer-events-none absolute inset-px rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        [style.background]="hoverGradient"
        [style.opacity]="gradientOpacity"
      ></div>
      
      <!-- Content -->
      <div class="relative">
        <ng-content></ng-content>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
    
    .bg-border {
      background-color: hsl(var(--border));
    }
    
    .bg-background {
      background-color: hsl(var(--background));
    }
    
    .group:hover .group-hover\\:opacity-100 {
      opacity: 1;
    }
  `]
})
export class MagicCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() className?: string;
  @Input() gradientSize: number = 200;
  @Input() gradientColor?: string;
  @Input() gradientOpacity?: number;
  @Input() gradientFrom?: string;
  @Input() gradientTo?: string;
  @Input() isDarkTheme = false;

  @ViewChild('cardRef', { static: true }) cardRef!: ElementRef<HTMLDivElement>;

  private mouseX: number = -this.gradientSize;
  private mouseY: number = -this.gradientSize;
  private animationFrameId?: number;
  private isMouseInside = false;

  borderGradient = '';
  hoverGradient = '';

  // Default colors for light and dark themes
  private defaultColors = {
    light: {
      gradientColor: 'rgba(255, 103, 94, 0.15)',
      gradientOpacity: 0.5,
      gradientFrom: 'rgba(199, 199, 199, 0.2)',
      gradientTo: 'rgba(255, 103, 94, 0.25)',
    },
    dark: {
      gradientColor: 'rgba(68, 188, 255, 0.15)',
      gradientOpacity: 0.4,
      gradientFrom: 'rgba(68, 188, 255, 0.2)',
      gradientTo: 'rgba(255, 103, 94, 0.25)',
    }
  };

  ngOnInit() {
    this.updateGradients();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isDarkTheme']) {
      this.updateGradients();
    }
  }

  ngOnDestroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  handleMouseEnter() {
    this.isMouseInside = true;
    this.mouseX = -this.gradientSize;
    this.mouseY = -this.gradientSize;
    this.updateGradients();
  }

  handleMouseLeave() {
    this.isMouseInside = false;
    this.mouseX = -this.gradientSize;
    this.mouseY = -this.gradientSize;
    this.updateGradients();
  }

  handleMouseMove(event: MouseEvent) {
    if (!this.isMouseInside || !this.cardRef?.nativeElement) return;

    const rect = this.cardRef.nativeElement.getBoundingClientRect();
    this.mouseX = event.clientX - rect.left;
    this.mouseY = event.clientY - rect.top;

    // Use requestAnimationFrame for smooth animation
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    
    this.animationFrameId = requestAnimationFrame(() => {
      this.updateGradients();
    });
  }

  private updateGradients() {
    const currentTheme = this.isDarkTheme ? 'dark' : 'light';
    const defaults = this.defaultColors[currentTheme];
    
    const finalGradientColor = this.gradientColor || defaults.gradientColor;
    const finalGradientOpacity = this.gradientOpacity ?? defaults.gradientOpacity;
    const finalGradientFrom = this.gradientFrom || defaults.gradientFrom;
    const finalGradientTo = this.gradientTo || defaults.gradientTo;
    
    this.borderGradient = `radial-gradient(${this.gradientSize}px circle at ${this.mouseX}px ${this.mouseY}px, ${finalGradientFrom}, ${finalGradientTo}, var(--border) 100%)`;
    
    this.hoverGradient = `radial-gradient(${this.gradientSize}px circle at ${this.mouseX}px ${this.mouseY}px, ${finalGradientColor}, transparent 100%)`;
    
    // Update the gradient opacity
    this.gradientOpacity = finalGradientOpacity;
  }

  private detectTheme() {
    if (typeof window === 'undefined') return;
    
    // Check for manual theme classes first
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    
    // Common theme class patterns
    const isDarkClass = htmlElement.classList.contains('dark') || 
                       bodyElement.classList.contains('dark') ||
                       htmlElement.classList.contains('theme-dark') ||
                       bodyElement.classList.contains('theme-dark');
    
    const isLightClass = htmlElement.classList.contains('light') || 
                        bodyElement.classList.contains('light') ||
                        htmlElement.classList.contains('theme-light') ||
                        bodyElement.classList.contains('theme-light');
    
    // Check data attributes
    const dataTheme = htmlElement.getAttribute('data-theme') || bodyElement.getAttribute('data-theme');
    
    if (isDarkClass || dataTheme === 'dark') {
      this.isDarkTheme = true;
    } else if (isLightClass || dataTheme === 'light') {
      this.isDarkTheme = false;
    } else {
      // Fall back to system preference
      this.isDarkTheme = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
  }
}